import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GoogleMap, Marker, OverlayViewF } from '@react-google-maps/api';
import {
  CrosshairIcon,
  ExternalLinkIcon,
  HomeIcon,
  LocationBoldIcon,
  RefreshIcon,
} from '@src/svg';
import { TooltipWrapper } from '@uikit/TooltipWrapper';
import { LatLngLiteral } from '../geo';
import { placesIconsStyles } from '@component/Ride/constants';
import { LOCATION_PIN_ICON_URL } from '../mapStyles';
import { MapsGate } from '@component/GoogleMap/MapsGate';

const DEFAULT_CENTER: LatLngLiteral = { lat: 34.0522, lng: -118.2437 };
const DEFAULT_ZOOM = 17;
const EMPTY_ZOOM = 10;
const PAN_DURATION_MS = 700;
const ZOOM_STEP_MS = 220;

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

type LocationMapProps = {
  position: LatLngLiteral | null;
  /** Bump to force the marker back to `position` (e.g. after a geofence snap). */
  resetToken: number;
  onPinMove: (position: LatLngLiteral) => void;
  title: string;
  subtitle: string;
  message?: string;
  /** True while the pin sits at an address that differs from the fields. */
  showMatchAddress?: boolean;
  onMatchAddress?: () => void;
  dataTestId?: string;
};

const LocationMapInner: FC<LocationMapProps> = ({
  position,
  resetToken,
  onPinMove,
  title,
  subtitle,
  message,
  showMatchAddress,
  onMatchAddress,
  dataTestId,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const panFrameRef = useRef<number>(0);
  const zoomTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const initialViewRef = useRef({
    center: position ?? DEFAULT_CENTER,
    zoom: position ? DEFAULT_ZOOM : EMPTY_ZOOM,
  });

  const cancelMove = useCallback(() => {
    cancelAnimationFrame(panFrameRef.current);
    clearTimeout(zoomTimerRef.current);
  }, []);

  /** Glides the map to `target`, then steps the zoom to `zoom` if given. */
  const smoothMoveTo = useCallback(
    (target: LatLngLiteral, zoom?: number) => {
      const map = mapRef.current;
      if (!map) return;
      cancelMove();

      const stepZoom = () => {
        const current = map.getZoom() ?? DEFAULT_ZOOM;
        if (zoom === undefined || current === zoom) return;
        map.setZoom(current + Math.sign(zoom - current));
        zoomTimerRef.current = setTimeout(stepZoom, ZOOM_STEP_MS);
      };

      const from = map.getCenter();
      if (
        !from ||
        (Math.abs(from.lat() - target.lat) < 1e-7 &&
          Math.abs(from.lng() - target.lng) < 1e-7)
      ) {
        stepZoom();
        return;
      }

      const start = { lat: from.lat(), lng: from.lng() };
      const startedAt = performance.now();
      const frame = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / PAN_DURATION_MS);
        const eased = easeInOutCubic(progress);
        map.setCenter({
          lat: start.lat + (target.lat - start.lat) * eased,
          lng: start.lng + (target.lng - start.lng) * eased,
        });
        if (progress < 1) {
          panFrameRef.current = requestAnimationFrame(frame);
        } else {
          stepZoom();
        }
      };
      panFrameRef.current = requestAnimationFrame(frame);
    },
    [cancelMove]
  );

  useEffect(() => cancelMove, [cancelMove]);

  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      gestureHandling: 'cooperative',
      keyboardShortcuts: false,
      styles: placesIconsStyles,
    }),
    []
  );

  const markerIcon = useMemo<google.maps.Icon>(
    () => ({
      url: LOCATION_PIN_ICON_URL,
      scaledSize: new google.maps.Size(32, 41),
      anchor: new google.maps.Point(16, 41),
    }),
    []
  );

  const positionKey = position ? `${position.lat},${position.lng}` : '';

  useEffect(() => {
    if (!position) return;
    const zoom = mapRef.current?.getZoom() ?? DEFAULT_ZOOM;
    smoothMoveTo(position, Math.max(zoom, 15));
  }, [positionKey]);

  // The marker keeps its dragged spot when the position state is unchanged, so
  // force it back explicitly (geofence snap-back).
  useEffect(() => {
    if (!resetToken || !position) return;
    markerRef.current?.setPosition(position);
    smoothMoveTo(position);
  }, [resetToken]);

  const handleDragEnd = useCallback(
    (event: google.maps.MapMouseEvent) => {
      setIsDragging(false);
      if (!event.latLng) return;
      onPinMove({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    },
    [onPinMove]
  );

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      onPinMove({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    },
    [onPinMove]
  );

  const zoomBy = (delta: number) => {
    const map = mapRef.current;
    if (map) map.setZoom((map.getZoom() ?? DEFAULT_ZOOM) + delta);
  };

  const recenter = () => {
    if (position) smoothMoveTo(position, DEFAULT_ZOOM);
  };

  return (
    <div className="location_map" data-test-id={`${dataTestId}-map`}>
      <GoogleMap
        mapContainerClassName="location_map__canvas"
        center={initialViewRef.current.center}
        zoom={initialViewRef.current.zoom}
        options={mapOptions}
        onClick={handleMapClick}
        onLoad={(map) => {
          mapRef.current = map;
        }}
        onUnmount={() => {
          mapRef.current = null;
        }}
      >
        {position && !isDragging && (
          <OverlayViewF
            position={position}
            mapPaneName="overlayLayer"
            getPixelPositionOffset={(width, height) => ({
              x: -width / 2,
              y: -height / 2,
            })}
          >
            <div className="location_map__pulse" aria-hidden="true">
              <span />
              <span />
            </div>
          </OverlayViewF>
        )}
        {position && (
          <Marker
            position={position}
            draggable
            icon={markerIcon}
            onLoad={(marker) => {
              markerRef.current = marker;
            }}
            onUnmount={() => {
              markerRef.current = null;
            }}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
          />
        )}
      </GoogleMap>

      {position && (
        <a
          className="location_map__view"
          href={`https://www.google.com/maps?q=${position.lat},${position.lng}`}
          target="_blank"
          rel="noopener noreferrer"
          data-test-id={`${dataTestId}-map-view-in-maps`}
        >
          <LocationBoldIcon width={16} height={16} color="currentColor" />
          View in Maps
          <ExternalLinkIcon width={14} height={14} color="currentColor" />
        </a>
      )}

      <div className="location_map__controls">
        <div className="location_map__zoom">
          <button
            type="button"
            aria-label="Zoom in"
            data-test-id={`${dataTestId}-map-zoom-in`}
            onClick={() => zoomBy(1)}
          >
            +
          </button>
          <button
            type="button"
            aria-label="Zoom out"
            data-test-id={`${dataTestId}-map-zoom-out`}
            onClick={() => zoomBy(-1)}
          >
            &minus;
          </button>
        </div>
        <button
          type="button"
          className="location_map__recenter"
          aria-label="Re-center on pin"
          disabled={!position}
          data-test-id={`${dataTestId}-map-recenter`}
          onClick={recenter}
        >
          <CrosshairIcon width={18} height={18} color="currentColor" />
        </button>
        {showMatchAddress && (
          <div className="location_map__match_wrap">
            <TooltipWrapper
              tooltip="Match address to updated pin location"
              disableChildrenWrapper
            >
              <button
                type="button"
                className="location_map__match"
                aria-label="Match address to updated pin location"
                data-test-id={`${dataTestId}-map-match-address`}
                onClick={onMatchAddress}
              >
                <RefreshIcon width={18} height={18} color="currentColor" />
              </button>
            </TooltipWrapper>
          </div>
        )}
      </div>

      {position && title && (
        <div
          className="location_map__card"
          data-test-id={`${dataTestId}-map-address-card`}
        >
          <span className="location_map__card_icon">
            <HomeIcon width={20} height={20} color="#ffffff" />
          </span>
          <div className="location_map__card_text">
            <strong>{title}</strong>
            <span>{subtitle}</span>
          </div>
        </div>
      )}

      {message && (
        <div
          className="location_map__message"
          role="alert"
          data-test-id={`${dataTestId}-map-message`}
        >
          {message}
        </div>
      )}
    </div>
  );
};

/** Gated so the location modal still works (address search/typing) when Google Maps can't load. */
const LocationMap: FC<LocationMapProps> = (props) => (
  <MapsGate height={360}>
    <LocationMapInner {...props} />
  </MapsGate>
);

export default LocationMap;
